import React from 'react';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import { Button } from '@repo/ui';
// `CloseIcon`, `ListFilter` and `ArrowDownUp` left with the Filter/Sort/clear
// markup — they are `ToolbarControl`'s glyphs now, declared once beside the
// buttons that use them.
import { CheckSquare, Columns3, X as XIcon } from 'lucide-react';
import { toolbarControlSx, TOOLBAR_ICON_SIZE } from 'components/UI/fieldMetrics';
import { ToolbarSearchField } from 'components/UI/ToolbarSearchField';
// Direct path, not the `components/UI` barrel: that file is append-at-end and was
// the highest-conflict line in the repo while this landed.
import { FilterControl, SortControl, TOOLBAR_ICON_STROKE } from 'components/UI/ToolbarControl';
import { glassSx } from 'utils/glass';
// The page's content cap, imported rather than repeated: the search field's width
// is derived from it (see the maxWidth note below), so a change there must reach here.
// APP_MAX_WIDTH is the SHELL cap, which is what `layouts/Default` applies and what
// the field's `calc` is measuring against. Equal to `CONTENT_MAX_WIDTH` today.
import { APP_MAX_WIDTH, SIDE_NAV_WIDTH } from 'constants/layout';

export type HeaderBarProps = {
  isMobile: boolean;
  title: string;
  /**
   * How many certificates the current filters match — rendered beside the title.
   *
   * A count was the one thing the numbered pager communicated that infinite
   * scroll does not: "page 3 of 7" told you roughly how many you had. Dropping
   * the pager without replacing that would have lost a real fact, so it moves
   * to the title, where it answers the question directly instead of by
   * multiplication.
   *
   * It is the FILTERED total, not a lifetime one, so it reads as "this is what
   * you are looking at" rather than contradicting the list beneath it.
   * `undefined` while the first page is still loading — a count that starts at
   * 0 and jumps is worse than one that arrives.
   */
  count?: number;
  onSearchChange: (value: string) => void;
  onFilterClick: () => void;
  filterBadgeCount: number;
  /** Clear every applied filter — the welded X on the Filter control. */
  onFilterClear: () => void;
  sortButtonRef: React.RefObject<HTMLButtonElement>;
  onSortClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  sortBadgeCount: number;
  showSortClear: boolean;
  onSortClear: () => void;
  /** Multi-select mode. Promoted out of the overflow menu — see the button below. */
  onSelectClick?: () => void;
  /**
   * Whether the selection panel is currently open.
   *
   * Drives the button's glyph and label. The control is a TOGGLE — pressing it
   * again closes the panel — and a button that looks identical in both states
   * does not say so, which is how it read as one-way.
   */
  selectOpen?: boolean;
  /** Which fields each card shows. Was "Displayed data" inside the overflow menu. */
  onColumnsClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  /**
   * Rendered INSIDE the sticky container, beneath the controls row.
   *
   * The selection bar belongs here rather than in the page body: it is chrome for
   * a mode the toolbar turns on, so it has to stay reachable while you scroll the
   * list you are selecting from — scrolling away from "Select all" mid-selection
   * is the whole problem. It also means the one bottom border belongs to the
   * block as a whole, instead of the header drawing a rule across the middle of
   * a control group.
   */
  belowRow?: React.ReactNode;
};

/**
 * One treatment for every control in the toolbar row: white surface, one light
 * border, 48px tall. The row previously mixed a white bordered search field with
 * filled `#E3E6E7` buttons — peers in the same row styled as two different things,
 * and the grey read heavier than anything else on the page. White-with-a-border
 * keeps them legible as buttons while letting the CARDS carry the visual weight,
 * which matters more in an app embedded inside a brand's own site.
 */
export const HeaderBar: React.FC<HeaderBarProps> = ({
  isMobile,
  title,
  count,
  onSearchChange,
  onFilterClick,
  filterBadgeCount,
  onFilterClear,
  sortButtonRef,
  onSortClick,
  sortBadgeCount,
  showSortClear,
  onSortClear,
  onSelectClick,
  selectOpen = false,
  onColumnsClick,
  belowRow,
}) => {
  // Border only once the list has scrolled under the bar. At rest a hairline
  // under a bar flush with the content above it reads as a stray line; it earns
  // its place only when it is separating two things.
  //
  // IntersectionObserver, not a scroll listener. Observing the sticky element
  // with a -1px top margin makes its intersection ratio drop below 1 exactly when
  // it becomes stuck, which is the state we actually care about — a scroll
  // handler only approximates it with a magic offset, fires on every frame of
  // every scroll, and depends on scroll events being delivered at all (they are
  // not, in a document the browser considers hidden).
  const theme = useTheme();
  /*
   * A SENTINEL, not the bar itself — and this is the whole fix.
   *
   * The previous version observed the sticky element with `threshold: [1]` and a
   * -1px top rootMargin, which is a real technique but only works when the sticky
   * element has `top: -1px`, so that becoming stuck clips it and drops the ratio
   * below 1. This bar has `top: 0` AND is the first thing on the page, so it sits
   * at viewport y=0 whether it is stuck or not — measured identical at scrollY 0
   * and 600. There was never a transition to observe.
   *
   * The failure was not "no border": the -1px margin clipped the bar at rest too,
   * so the ratio was permanently below 1 and the hairline was permanently ON —
   * exactly the stray line under a flush bar that the state exists to prevent.
   *
   * A zero-height sentinel in the flow directly above the bar does have somewhere
   * to go: it scrolls out of view precisely when the bar becomes stuck. Observing
   * THAT gives a real transition, with no magic offsets and no dependency on
   * scroll events firing.
   */
  const sentinelRef = React.useRef<HTMLDivElement>(null);
  const [scrolled, setScrolled] = React.useState(false);
  React.useEffect(() => {
    const el = sentinelRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(([entry]) => setScrolled(!entry.isIntersecting), {
      threshold: 0,
    });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <>
      {/*
        1px tall, pulled back by -1px, so it costs no layout but still has AREA.
        A zero-height sentinel is unobservable: IntersectionObserver computes a
        ratio against the target's box, and a zero-area target never registers an
        intersection — verified here, where a fresh observer on the 0px version
        fired nothing at all across a full scroll. `aria-hidden` because it is a
        measurement device, not content.
      */}
      <Box ref={sentinelRef} aria-hidden sx={{ height: '1px', mb: '-1px' }} />
    <Stack
      sx={{
        // Sticky: search + filters are what you reach for WHILE scanning the
        // list, so they should not scroll away with it.
        position: 'sticky',
        top: 0,
        zIndex: 10,
        padding: { xs: '24px 16px 0', md: '24px 16px 0' },
        /*
         * LIQUID GLASS ONCE STUCK, matching the floating bottom nav and the
         * certificate page's own header — the same material, the same numbers.
         *
         * This bar was the last persistent chrome still painting an opaque fill,
         * so a list scrolling under it met a hard white lid while the bar at the
         * other end of the same screen let the page show through. `saturate` is
         * the half that does the work: blur alone greys out what is behind it and
         * reads as frosted plastic, while restoring saturation keeps the content's
         * colour and reads as glass.
         *
         * UNCONDITIONAL, like the bottom nav's — not gated on `scrolled`. Two
         * reasons. At rest the bar sits over the page background, where 82% white
         * over white is indistinguishable from opaque, so gating buys nothing
         * visible. And `scrolled` does not currently flip: measured at scrollY
         * 500 with the list visibly clipped under the bar, its IntersectionObserver
         * still reported unstuck and the bar's own hairline border stayed
         * transparent. Gating the material on that flag would have shipped a glass
         * effect that never appeared. The border bug is left alone here — it is
         * pre-existing and wants its own fix, not a workaround buried in a
         * background colour.
         */
        /*
         * `active` defaulted to true — UNCONDITIONAL, per the reasoning above.
         * The shared helper carries the flag for the bars that gate on stuck;
         * this one deliberately does not, and adopting the token must not
         * quietly introduce a gate that was argued against.
         *
         * ONE COLOUR CHANGE, deliberate: the `@supports` fallback was
         * `background.default` (#F7F7F8, or a brand's own `bg_color`) and is now
         * `background.paper` (#fff), because that is what the shared token uses.
         * It is the more faithful degradation of the two — the live glass is 82%
         * paper over the page, which against #F7F7F8 resolves to very nearly
         * white, so an opaque WHITE lid is closer to what the supported path
         * actually renders than the page colour is. Only browsers without
         * backdrop-filter see it at all.
         */
        ...glassSx(theme),
        // The `!important` is the operative part (the shorthand is incidental).
        // loses to `#vera-shadow-root * { border-color: hsl(var(--border)) }` in
        // index.css, whose ID specificity beats emotion's single-class sx styles —
        // so the rule rendered at that grey permanently, visible at rest, which is
        // exactly what the scrolled check exists to prevent.
        //
        // (Earlier comments here blamed Tailwind preflight. That was wrong:
        // preflight is disabled in tailwind.config.js. See index.css.)
        borderBottom: scrolled
          ? '1px solid #E4E4E7 !important'
          : '1px solid transparent !important',
        // AFTER the glass spread, so this wins over the token's own
        // `background-color` transition. Intentional: the fill here never
        // changes (see above), so there is nothing for it to animate, while the
        // border does flip and must keep its ease.
        transition: 'border-color .15s ease',
      }}
    >
      {/*
        ONE wrapping flex row, re-ordered per breakpoint — NOT two layouts with
        duplicated buttons. Duplication was the obvious approach and is wrong
        here: `sortButtonRef` anchors the sort popover, and with two copies in
        the DOM the ref lands on whichever rendered last (the hidden desktop
        one), so on a phone the popover would anchor to a `display:none`
        element. A zero-height flex break plus `order` gets both layouts from a
        single render of each control.

          mobile  : [ title ][ filter sort layout ]
                    [ search .................... ][ select ]

          desktop : [ title ][ menu ]
                    [ search ][ select ] ....... [ filter sort layout ]

        The desktop line is `ActionLine`'s arrangement: a left cluster that ends
        at the field's max-width, a right cluster hard against the edge, slack
        between them. On a phone there is no slack to distribute, so the two
        clusters split across the two lines instead and the search field takes
        the whole width it can get.
      */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          // 8, was 16. Measured, the title row's bottom to the search field's top
          // was 32px — the row gap plus the field's own leading — which read as a
          // gap between two unrelated blocks rather than one header unit.
          rowGap: '8px',
          columnGap: '8px',
          mb: '16px',
        }}
      >
        <Typography
          variant='h1'
          sx={{
            order: 0,
            flexGrow: 1,
            fontWeight: 400,
            // 22 on a phone. Measured against the 343px content width:
            //   controls 48 + title 24px -> 359  overflows
            //   controls 44 + title 24px -> 343  exactly, no gap left
            //   controls 44 + title 22px -> 331  fits with 12px slack
            // So 22 is the largest step that keeps 44px tap targets. 24 is only
            // reachable by dropping the controls to 40, which is below the tap
            // minimum — a real cost for a cosmetic gain.
            fontSize: { xs: '22px', md: '24px' },
          }}
        >
          {title}
          {typeof count === 'number' && count > 0 && (
            <Box
              component='span'
              sx={theme => ({
                marginLeft: '8px',
                color: theme.palette.text.secondary,
                // Deliberately NOT the title's size. It is an annotation ON the
                // title, and at the same size it reads as part of the name —
                // "My Certificates 24" rather than a heading with a count.
                fontSize: { xs: '16px', md: '18px' },
              })}
            >
              {count}
            </Box>
          )}
        </Typography>
        {/*
          The desktop hamburger is GONE. It was `xs:'none', md:'inline-flex'` —
          desktop-only, on the reasoning that the bottom bar owned navigation on a
          phone and a hamburger there would be a second route to the same
          destinations.

          The rail (`components/UI/SideNav`, fed by `components/UI/primaryNav`)
          removed the other half of that argument. Certificates, Activity and
          Profile are rail destinations, and Log out is pinned at the rail's
          bottom — so this menu had decayed to a single item that the rail already
          offered, one click further away. Nothing became unreachable when it went.

          The `ActionMenu` it used to open is still mounted below and is now
          unreachable by design: its `Transfer history` and `Help center` branches
          in `useCertificatesPageState` are the Transfer epic's code anchors and
          are deliberately preserved rather than deleted.
        */}

        {/* Zero-height full-width break: forces the wrap so the search row starts
            a new row at both breakpoints. */}
        <Box sx={{ order: 3, flexBasis: '100%', height: 0 }} />

        {/*
          THE LEFT GROUP — search + Select — arranged like `apps/platform`'s
          `ActionLine`: one left cluster (its bulk-select toggle + the search
          field), one right cluster (Filter / Sort / Layout), the space between
          them left empty.

          `flexGrow: 1` on this group is what does the right-alignment, and it is
          deliberately not `justifyContent: 'space-between'` on the wrapping row:
          that row also carries the TITLE line, where the distribution would be a
          second thing to reason about. Growing this group instead consumes the
          slack on the search line only, so the controls group is pushed hard
          right there and nothing above it moves. The search field inside caps at
          its own max-width, so the slack lands between Select and Filter rather
          than stretching the field across a 1280px viewport.

          One arrangement at BOTH breakpoints — search first, Select after it.
          `ActionLine` puts its bulk toggle to the LEFT of the field, but for a
          functional reason that does not exist here: it aligns with the table's
          leading checkbox column. These are cards, which grow a checkbox inline,
          so there is no column to align to — and keeping the phone's existing
          order avoids a mobile change for a desktop request.
        */}
        <Box
          sx={{
            order: 4,
            display: 'flex',
            alignItems: 'center',
            // 8px, and it stays 8 while the controls group opposite goes to 12
            // on desktop: this is the gap INSIDE one unit (a field and the
            // control paired with it), which `ActionLine` also holds at 8 while
            // spacing its units at 12.
            gap: '8px',
            flexGrow: 1,
            minWidth: 0,
          }}
        >
          <Stack
            sx={{
              flexGrow: 1,
              minWidth: 0,
              /*
                ONE CARD WIDE, derived from the page rather than pinned to a number,
                so the field's right edge lands on the first card's right edge and
                the toolbar shares a left column with the content beneath it.

                MEASURED, because the obvious form of this is wrong: `calc((100% -
                32px) / 3)` resolves 100% against this Stack's containing block —
                the LEFT GROUP, which is `flexGrow: 1` and therefore the row minus
                the controls opposite (861px at a 1280 viewport). That produced a
                276px field against a 432px card edge. The percentage has to be
                taken from the PAGE, not from the flex parent, so it is expressed in
                viewport units instead.

                The chain: `layouts/Default` caps content at APP_MAX_WIDTH with a
                24px gutter each side, so the row is `min(100vw, 1280px) - 48px`.
                `CertificatesGrid` is `<Grid item xs md={4}>` with `spacing={2}` —
                three columns at md+, separated by two 16px gutters — so one card is
                `(row - 32px) / 3`. Above the cap that is (1232 - 32) / 3 = 400.

                spacing={2} is 16px because `apps/app` is on MUI's DEFAULT 8px base,
                not `@repo/ui`'s 4px (§29).

                ⚠️ APP_MAX_WIDTH here is CORRECT and deliberate. It is the same number
                as `CONTENT_MAX_WIDTH` — there is one content width now — but this
                expression describes the SHELL the column is centred in, which is what
                `layouts/Default` caps, so the shell constant is the honest one to
                name. Nothing to substitute either way.

                ⚠️ The 48 is `PAGE_GUTTER` DOUBLED, and the page gutter and this
                expression are two halves of one number with nothing but this comment
                linking them. On THIS page the gutter is delivered as 8 on the page
                container plus 16 from each of this bar's own padding and the grid
                wrapper's `p-4`. Change the gutter and this must change in the same
                edit, or the field stops landing on the card's edge — it was 66.67px
                out earlier for exactly this reason. (It was `96px` while this page
                carried its own wider 48px gutter; that split is gone.)

                Below md the grid is 1–2 columns and the field is the row's only
                flexible element, so no cap applies.
              */
              maxWidth: {
                /*
                  `100vw - SIDE_NAV_WIDTH`, NOT `100vw`. The viewport stopped being
                  the content column when the desktop rail landed in flow: between
                  `md` and `APP_MAX_WIDTH + SIDE_NAV_WIDTH` the column is
                  `100vw - SIDE_NAV_WIDTH`, so the bare `100vw` proxy overshot and
                  the field measured 66.67px past the card's right edge at 1280.
                  Above that cap the rail sits in the page's own margin and both
                  forms agree, which is exactly why this stayed wrong quietly — it
                  only breaks in the middle band.

                  The cap is stated as the EXPRESSION, not as a number, because it
                  moves with the rail EVERY time the rail moves: 1480 at a 200 rail,
                  1520 when `SIDE_NAV_WIDTH` went 200 -> 240 on 2026-08-23, and 1536
                  when it went 240 -> 256 on 2026-08-31. A hardcoded number here would
                  have gone stale silently — twice, by now. The `calc` below already
                  interpolates the constant, so the alignment itself has survived both
                  changes untouched.

                  MEASURED 2026-08-31 at the current 256 rail, field right edge against
                  first card right edge: both 594.7 at a 1280 viewport, both 712 at
                  1600. (The 1280 pair reads 594.7 rather than the 592 an earlier note
                  recorded, because the content column narrowed by the same 16px the
                  rail gained. Equality is the invariant this comment is about; the
                  absolute value is expected to move whenever the rail does.)
                */
                md: `calc((min(100vw - ${SIDE_NAV_WIDTH}px, ${APP_MAX_WIDTH}px) - 48px - 32px) / 3)`,
              },
            }}
          >
            {/*
              The treatment that used to be ~25 lines of inline `sx` here now
              lives in `ToolbarSearchField`, because the Activity feed needed the
              same field and a second copy would have drifted (§36 — the same
              move `toolbarControlSx` already represents for the buttons in this
              very row).

              NO `debounceMs`: this search filters certificates ALREADY LOADED in
              memory, so there is no request to spare and a delay would only make
              the list feel laggy. The Activity feed passes one because its search
              is server-side. That difference is the reason the prop exists rather
              than the component debouncing unconditionally.
            */}
            <ToolbarSearchField
              onChange={(value: string) => onSearchChange(value)}
              ariaLabel='Search certificates'
            />
          </Stack>

          {/*
            SELECT BELONGS TO THE SEARCH GROUP, not to Filter/Sort/Layout — and as
            of this change the LAYOUT says so at both breakpoints, which is the
            point. Filter, Sort and Layout all change how the SAME list is
            presented and leave you doing the same thing afterwards. Select changes
            what the screen IS: every card grows a checkbox, a toolbar appears, and
            tapping a card no longer opens it. A mode switch sitting inside a row
            of view controls reads as a fourth view control.

            The comment used to make that argument while the desktop layout
            contradicted it — Select rendered at the END of the Filter/Sort/Layout
            run (`order: { md: 6 }`, after the controls group's `md: 5`), so the
            reasoning held on a phone only. It now sits inside the left group at
            every width, opposite the view controls rather than appended to them.

            Keeping it here also settles the earlier trade-off between title size
            and tap targets for free: it returns ~52px to the title row, so the
            title can breathe without any control dropping below the 44px minimum.

            It matches the SEARCH FIELD's 48px rather than the 44px view controls,
            because it shares a row and a baseline with the field — a height
            mismatch is only visible between neighbours, and these are neighbours.
          */}
          <Box sx={{ flexShrink: 0 }}>
            <Button
              variant='tertiary'
              size='large'
              onClick={onSelectClick}
              aria-label={selectOpen ? 'Close selection' : 'Select'}
              sx={{ ...toolbarControlSx(theme), height: 48, minWidth: 48 }}
            >
              {/*
                X while the panel is OPEN. The button toggles it shut, and the
                checkmark glyph said the same thing in both states — so the second
                press looked like a repeat of the first rather than its undo, which
                is exactly why the panel's own X was previously the only exit
                anyone found. `X` is this app's dismiss glyph for a transient
                surface (working-conventions §38); the panel is not a record being
                deleted, so a trash can would overstate it.
              */}
              {selectOpen ? (
                <XIcon size={TOOLBAR_ICON_SIZE} strokeWidth={TOOLBAR_ICON_STROKE} />
              ) : (
                <CheckSquare size={TOOLBAR_ICON_SIZE} strokeWidth={TOOLBAR_ICON_STROKE} />
              )}
              {/*
                Same treatment as Filter / Sort / Layout: the house 1px stroke and
                the Button's inherited 16/500 label. This button sits in the SAME
                visual row as that cluster, so a heavier glyph or a smaller label
                here would read as an outlier even though it belongs to the search
                group rather than the filter group.
              */}
              <Box
                component='span'
                sx={{
                  ml: 1,
                  whiteSpace: 'nowrap',
                  display: { xs: 'none', md: 'inline' },
                }}
              >
                {selectOpen ? 'Close' : 'Select'}
              </Box>
            </Button>
          </Box>
        </Box>

        <Stack
          direction={'row'}
          /*
           * 12px on desktop, 8 on a phone — a PIXEL target, converted for this
           * app. `apps/app` never overrides MUI's 8px spacing base (working-
           * conventions §29), so `1.5` here is the 12px that `spacing={3}` gives
           * on the platform's 4px base; copying the platform's `3` would render
           * 24. 12 is the button-group gap the platform's own `ActionLine` uses
           * for this exact trio.
           *
           * Mobile stays at 8: these three are icon-only there and share the
           * title's row, where 343px of content width is already spoken for by
           * three 44px tap targets and the title.
           */
          spacing={{ xs: 1, md: 1.5 }}
          alignItems={'center'}
          sx={{ order: { xs: 2, md: 5 }, flexShrink: 0 }}
        >
          {/*
            The badge is an OVERLAY on the control, not text inside it.

            `filterBadgeCount` and `sortBadgeCount` were destructured here and never
            rendered, so an active filter had no visible sign — the same dead-prop gap
            SearchFilterSortBar had. The obvious fix, an inline "(1)" beside the label,
            is what the label row already tried: it lives in a
            `display:{xs:'none',md:'inline'}` span, so it vanishes at 375px, which is
            the width that matters most here.

            The count is now an INLINE pill inside the button (`CountPill`),
            matching the platform, which renders it that way in both of its
            toolbar controls. It was a corner overlay; that could not survive the
            clear-X being welded onto the sort button, because a badge on the
            group's corner labels the wrong half. See `CountBadge`.

            CountPill renders NOTHING at count <= 0 — no element, no layout shift —
            so these stay byte-identical in geometry when no filter is applied.
          */}
          {/*
            Filter and Sort are `components/UI/ToolbarControl` — the SAME two
            components the certificate sub-views render (Provenance / Scans /
            Activity), which is the whole point. This row and that one had each
            hand-rolled the pair and drifted: measured at 1280 on a clean tree,
            Sort was 90x48 here and 82x49 there. Read that file for the rest of
            the divergence and why the fix is a component rather than a comment.

            The clear-sort affordance is rendered BY `SortControl`, WELDED to the
            sort button as the right half of a split control — not as a sibling
            in this Stack. It used to be a sibling, which meant this Stack's 12px
            gap sat between them and the X read as a fourth toolbar button rather
            than part of Sort. The Stack's gap now separates the whole control
            from Filter and Layout, which is what it was for. It is desktop-only
            through `display` rather than the `isMobile` prop, because
            `useMediaQuery` does not re-evaluate on a live resize in this app.
          */}
          {/* The clear-X is rendered BY `FilterControl`, welded to it as the
              right half of a split control — the same `@repo/ui` `SplitControl`
              Sort uses, so the two are siblings by construction. It is out only
              when something is actually applied. */}
          <FilterControl
            count={filterBadgeCount}
            onClick={onFilterClick}
            subject='certificates'
            showClear={filterBadgeCount > 0}
            onClear={onFilterClear}
          />

          <SortControl
            count={sortBadgeCount}
            onClick={onSortClick}
            subject='certificates'
            showClear={showSortClear}
            onClear={onSortClear}
            // Was declared, destructured, and never attached to anything — the
            // popover anchored off `e.currentTarget` instead and this prop was
            // dead. `SortControl` forwards it to the button.
            buttonRef={sortButtonRef}
          />

          {/*
            LABELLED on desktop, like every other control in the row. It was the
            one icon-only button among labelled peers, which reads as an
            unfinished control rather than a deliberate one — the icon column
            being consistent top to bottom is the same argument §21 makes for
            menu rows, applied to a button row.

            Still icon-only on a phone, for the reason the label carries on
            Filter and Sort: at 375px the row cannot hold three labelled controls
            beside the title.

            `aria-label` is now the visible word rather than a longer
            description. A visible label and a different accessible name is a
            WCAG 2.5.3 mismatch — "click Layout" would not match the name a voice
            control resolves — and Filter/Sort already pair their visible word
            with the identical aria-label.
          */}
          <Button
            variant='tertiary'
            size='large'
            onClick={onColumnsClick}
            aria-label='Layout'
            title='Choose which details to show'
            sx={toolbarControlSx(theme)}
          >
            <Columns3 size={TOOLBAR_ICON_SIZE} strokeWidth={TOOLBAR_ICON_STROKE} />
            {/*
              No `fontSize` / `color`. Inherits the `@repo/ui` Button's own label
              typography (16px / 500), which is what Filter and Sort now do via
              `ToolbarLabel` and what the platform's equivalent span does. The
              pinned `14px` here was half of why this row read lighter and
              smaller than the console it mirrors.
            */}
            <Box
              component='span'
              sx={{
                ml: 1,
                whiteSpace: 'nowrap',
                display: { xs: 'none', md: 'inline' },
              }}
            >
              Layout
            </Box>
          </Button>
        </Stack>
      </Box>
      {belowRow}
    </Stack>
    </>
  );
};

export default HeaderBar;
